import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import InputField from "components/form/InputField";
import { VisaIcon } from "icons/payments/VisaIcon";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import { valuesProps } from "types/paymentMethods";
import DiscoverIcon from "icons/payments/Discover";
import UnionPay from "icons/payments/UnionPay";
import JCB from "icons/payments/JCB.svg";
import amex from "icons/payments/amex.svg";
import dinersClub from "icons/payments/dinersClub.svg";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { SettingsValue } from "types/payments";
import InputSelect from "components/form/InputSelect";
import {
  countryListGet,
  useCountryList,
} from "store/slices/paymentMethodSlice";
import { AddressDetails, countryAddressList } from "utils/countryAddressList";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  checkAddress,
  checkPhone,
  checkZip,
  detectCountry,
  formatCurrency,
  formatMinAndMaxAmount,
  getCountry3digitCode,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import { countryAndState } from "store/slices/documentsSlice";
import { PhoneInput } from "types/common";
import MobileInputNew from "components/form/MobileInputNew";
import {
  getCountryCallingCode,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { CountryCode } from "types/UserProfile";
import { useUserData } from "store/slices/authSlice";
import Button from "components/form/Button";
import { RootState } from "store/store";
import { VerifyStaxCardPaymentParams } from "types/agreement";
import { ConvenienceFeeDisplay } from "./PaymentMethodComponents";
import { getPaymentTotalWithFee } from "utils/paymentFee";

declare global {
  interface Window {
    StaxJs: any;
  }
}

interface CardFormProps {
  handleChange: (e: any, value: string) => void;
  isEdit?: boolean | undefined;
  values: valuesProps;
  iconType: string;
  selectedState: any;
  setSelectedState: any;
  cardErrMsg?: string | "";
  setEmail?: (value: string) => void;
  email?: string;
  setShowPreview?: any;
  isPaymentLink?: boolean;
  amount?: any;
  isRecurring?: boolean;
  paymentPlan?: any;
  isAgreement?: boolean;
  handleBack?: any;
  isUpdatePlan?: boolean;
  isSchedulePayment?: boolean;
  isRetry?: boolean;
  isUpdateSubscription?: boolean;
  checkNegativeBalance?: boolean;
  isProductLink?: boolean;
  accounts_id?: string | null;
  setIconType: (val: string) => void;
  collectAddress?: boolean;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
  handleRetryNext?: () => void;
  isUpdateSubscriptionPaymentMethod?: boolean;
  isDowngradePlan?: boolean;
  showConvFee?: boolean;
  description?: string | null;
  validateCard?: any;
  isErr?: number | -1;
  handleEmailBlur?: () => void;
  verifyStaxCardBank?: ({
    cc_token,
    cc_token2,
    x_method,
  }: VerifyStaxCardPaymentParams) => Promise<void>;
  disableInputFields?: boolean;
}

type CardBrand =
  | "visa"
  | "amex"
  | "mc"
  | "mastercard"
  | "discover"
  | "ds"
  | "jcb"
  | "diners"
  | "dinersclub"
  | "diners club"
  | "dc"
  | "unionpay"
  | string;

const StaxCardForm = ({
  handleChange,
  isEdit,
  values,
  iconType,
  selectedState,
  setSelectedState,
  validateCard = () => {},
  isErr,
  cardErrMsg,
  disableInputFields,
  amount,
  isRecurring,
  handleEmailBlur = () => {},
  setEmail = () => {},
  email,
  setShowPreview,
  isPaymentLink = true,
  paymentPlan,
  isAgreement = false,
  handleBack = () => {},
  isUpdatePlan = false,
  isSchedulePayment = false,
  isRetry = false,
  isUpdateSubscription = false,
  checkNegativeBalance = false,
  isProductLink = false,
  accounts_id = "",
  setIconType,
  collectAddress = true,
  collectPhone = false,
  isDowngradePlan = false,
  verifyStaxCardBank = async () => {},
  phoneNumber = {
    country_code: "",
    phone: "",
    postal_code: "",
  },
  setPhoneNumber = () => {},
  isUpdateSubscriptionPaymentMethod = false,
  description = "Online Payment",
  showConvFee = false,
  handleRetryNext = () => {},
}: CardFormProps) => {
  const [fieldArr, setFieldArr] = useState<string[]>();
  const [fieldObj, setFieldObj] = useState<AddressDetails>();
  const [isLoading, setIsLoading] = useState(false);
  const [states, setStates] = useState<any>([]);
  const [countriesWithStates, setCountriesWithStates] = useState<any[]>([]);
  const [cardFormComplete, setCardFormComplete] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);

  const staxRef = useRef<any>(null);
  const scriptLoaded = useRef(false);
  const initCountryRef = useRef(false);
  const autoDetectRef = useRef(false);

  const dispatch = useAppDispatch();
  const countryList = useCountryList();
  const {
    conFee,
    paymentPermission,
    isFeeLoading,
    generalSettings,
    checkedDuplicatePayment,
  } = useAppSelector((state: RootState) => state.payment);

  const { isChecking, isSubscriptionAllowed } = useAppSelector(
    (state: RootState) => state.subscription
  );
  const userData = useUserData();
  const portalSettings = usePortalSettingsData();
  const selectedCountryUI = values?.x_country ?? "";

  const amountLimit = useMemo(() => {
    if (
      userData?.data?.accountData?.balance &&
      generalSettings?.systemSettings
    ) {
      const { min, max } = formatMinAndMaxAmount(
        generalSettings?.systemSettings,
        userData?.data?.accountData?.balance
      );

      return { min, max };
    }

    return { min: 1.0, max: 100000.0 };
  }, [generalSettings, userData]);

  const amountCheck = useMemo(() => {
    if (isDowngradePlan || isUpdateSubscriptionPaymentMethod) {
      return false;
    }
    return amount < amountLimit.min || amount > amountLimit.max;
  }, [amount, amountLimit, isDowngradePlan, isUpdateSubscriptionPaymentMethod]);

  const isDisabled = useMemo<boolean>(() => {
    // Shown (cc_billing_address) is not the same as required
    // (cc_billing_addr_mandatory): with the address optional, ZIP alone is enough.
    const isAddressRequired = isProductLink
      ? collectAddress
        ? "Y"
        : "N"
      : portalSettings?.cc_billing_addr_mandatory;

    const addressField = checkAddress(
      portalSettings?.cc_billing_address,
      isAddressRequired,
      values,
      collectAddress
    );

    const validZip = checkZip(values);

    const isPhone = collectPhone ? checkPhone(phoneNumber) : true;

    return (
      !cardFormComplete ||
      tokenError !== null ||
      !addressField ||
      !values?.choldername ||
      !values?.choldername.trim() ||
      !isPhone ||
      !email ||
      !email.trim() ||
      !validateEmail(email) ||
      !validZip
    );
  }, [
    cardFormComplete,
    values,
    portalSettings,
    phoneNumber,
    email,
    tokenError,
  ]);

  const handleBadgeDisplay = (show?: boolean) => {
    const badge = document.querySelector(".grecaptcha-badge");
    if (badge && badge instanceof HTMLElement) {
      badge.style.display = show ? "block" : "none";
    }
  };

  const handleOnPhoneChange = useCallback((phone: string) => {
    setPhoneNumber((state) => ({
      ...state,
      phone,
    }));
  }, []);

  const handlePhoneCountryChange = useCallback((itm: string) => {
    setPhoneNumber((state) => ({
      ...state,
      country_code: getCountryCallingCode(itm as CountryCode),
    }));
  }, []);

  const onCountryListGet = async () => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      console.log("conntry list get err: -->", error?.message);
    }
  };

  useEffect(() => {
    let email = userData?.data?.accountData?.email || "";
    setEmail(email);
  }, []);

  useEffect(() => {
    if (scriptLoaded.current) return;

    const script = document.createElement("script");
    script.src = "https://staxjs.staxpayments.com/staxjs-captcha.js";
    script.async = true;

    script.onload = () => initStax();

    document.body.appendChild(script);
    scriptLoaded.current = true;

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (amountCheck || isDisabled) {
      handleBadgeDisplay();
    } else {
      handleBadgeDisplay(true);
    }
  }, [amountCheck, isDisabled]);

  useEffect(() => {
    autoDetectRef.current = false;
  }, []);

  const setCountry = async () => {
    const userCountry = await detectCountry();
    userCountry && handleChange({ value: userCountry }, "x_country");
  };

  useEffect(() => {
    if (countryList.length === 0) {
      onCountryListGet();
      return;
    }
    if (
      !isEdit &&
      !autoDetectRef.current &&
      (!values?.x_country || values?.x_country === "US")
    ) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [countryList]);

  const getStatesForCountryCode = (code: string) => {
    try {
      const countryMeta = countryList?.find((c: any) => c?.value === code);
      const countryName = countryMeta?.name;
      const found = countriesWithStates?.find(
        (c: any) =>
          c?.country_code?.toLowerCase() === code?.toLowerCase() ||
          (countryName &&
            c?.country_name?.toLowerCase() === countryName?.toLowerCase())
      );
      const st = found?.states || [];
      return st.map((s: any) => ({
        name: s?.state_name,
        value: s?.state_name,
        state_code: s?.state_code,
      }));
    } catch (e) {
      return [];
    }
  };

  const getPostalCode = () => {
    if (fieldArr && fieldArr?.includes("CEDEX")) return "CEDEX";
    if (fieldArr && fieldArr?.includes("ZIP")) return "ZIP";
    if (fieldArr && fieldArr?.includes("Eircode")) return "Eircode";
    if (fieldArr && fieldArr?.includes("PIN")) return "PIN";
    if (fieldArr && fieldArr?.includes("Postal code")) return "Postal code";

    return false;
  };

  const getStateName = () => {
    if (fieldArr && fieldArr?.includes("County")) return "County";
    if (fieldArr && fieldArr?.includes("State")) return "State";
    if (fieldArr && fieldArr?.includes("Department")) return "Department";
    if (fieldArr && fieldArr?.includes("District")) return "District";
    if (fieldArr && fieldArr?.includes("Emirate")) return "Emirate";
    if (fieldArr && fieldArr?.includes("Island")) return "Island";
    if (fieldArr && fieldArr?.includes("Oblast")) return "Oblast";
    if (fieldArr && fieldArr?.includes("Parish")) return "Parish";
    if (fieldArr && fieldArr?.includes("Prefecture")) return "Prefecture";
    if (fieldArr && fieldArr?.includes("Province")) return "Province";

    return false;
  };

  useEffect(() => {
    if (values?.x_country) {
      let data = countryAddressList()[values?.x_country || "US"];
      if (data) {
        const arr = Object.keys(data).map((field) => field);
        const obj = countryAddressList()[values?.x_country || "US"];
        setFieldArr(arr);
        setFieldObj(obj);
      }
    }
    if (isEdit && values?.x_country) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
      if (values?.x_state) {
        const selectedStateObject: any = nStates.find(
          (state: any) => state.name === values?.x_state
        );
        if (selectedStateObject) {
          setSelectedState(selectedStateObject);
          handleChange(selectedStateObject?.name, "x_state");
        }
      }
    } else if (isEdit && !values?.x_country) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [isEdit, values?.x_country]);

  useEffect(() => {
    if (
      values?.x_country &&
      countriesWithStates?.length > 0 &&
      countryList?.length > 0
    ) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
    }
  }, [values?.x_country, countriesWithStates, countryList]);

  const initStax = async () => {
    try {
      const webPaymentToken =
        paymentPermission?.cc_gateway_name?.key || "Test-Merchant-7e3f2cd81dbe";

      const stax = new window.StaxJs(webPaymentToken, {
        number: {
          id: "stax-card-number",
          placeholder: "1234 1234 1234 1234",
          style:
            "font-family:'DM Sans' !important;width:100%;height:100%;border:none;background:transparent;font-size:12px;",
        },
        cvv: {
          id: "stax-card-cvv",
          placeholder: "CVC",
          style:
            "font-family:'DM Sans' !important ;width:100%;height:100%;border:none;background:transparent;font-size:12px;",
        },
      });

      staxRef.current = stax;

      await stax.showCardForm();

      stax.on("card_form_complete", () => {
        setCardFormComplete(true);
        setTokenError(null);
        setErrorField(null);
        console.log("Card form complete");
      });

      stax.on("card_form_uncomplete", () => {
        setCardFormComplete(false);
        console.log("Card form incomplete");
      });

      console.log("Stax initialized");
    } catch (err) {
      console.error("Stax initialization error:", err);
    }
  };

  const getStateAndCountryCode = (countryCode: string, stateName: string) => {
    const ISOCode = getCountry3digitCode(countryCode as CountryCode);
    const statesList = states.find((s: any) => s.name === stateName);
    const stateCode = statesList?.state_code || "";

    return {
      country_code: ISOCode || countryCode,
      state_code: stateCode || stateName,
    };
  };

  const tokenizeCard = async () => {
    try {
      const [month, year] = values?.x_exp_date?.split("/") || ["", ""];
      const namesSplit = values?.choldername?.trim().split(" ") || [];

      const { country_code, state_code } = getStateAndCountryCode(
        values?.x_country || "US",
        values?.x_state || ""
      );

      let payload: any = {
        firstname: namesSplit?.[0] || userData?.data?.accountData?.first_name,
        lastname: namesSplit?.[1] || userData?.data?.accountData?.last_name,
        month: month?.trim(),
        year: `20${year?.trim()}`,
        person_name:
          values?.choldername ||
          `${userData?.data?.accountData?.first_name} ${userData?.data?.accountData?.last_name}`,
        // address_1: values?.x_address,
        // address_2: values?.address2 || "",
        // address_city: values?.x_city,
        // address_state: state_code,
        // address_zip: values?.x_zip,
        // address_country: country_code,
        email: email || userData?.data?.accountData?.email,
        method: "card",
        validate: false,
      };

      if (values?.x_address) {
        payload = {
          ...payload,
          address_1: values?.x_address,
        };
      }

      if (values?.x_city) {
        payload = {
          ...payload,
          address_city: values?.x_city,
        };
      }
      if (country_code) {
        payload = {
          ...payload,
          address_country: country_code,
        };
      }
      if (state_code) {
        payload = {
          ...payload,
          address_state: state_code,
        };
      }
      if (values?.x_zip) {
        payload = {
          ...payload,
          address_zip: values?.x_zip,
        };
      }

      if (collectPhone) {
        payload["phone"] = phoneNumber?.phone || "";
      } else if (userData?.data?.accountData?.phone) {
        payload["phone"] = userData?.data?.accountData?.phone || "";
      }

      const result = await staxRef.current.tokenize(payload);

      return result;
    } catch (err: any) {
      console.log("card error===", err);
      const error = err.fieldErrors || [];
      if (error?.length > 0) {
        setTokenError(error[0]?.message || "Card details error");
        setErrorField(error[0]?.field || null);
      }
    }
  };

  const imgUrl = (): React.ReactNode => {
    const brand: CardBrand = (
      values?.card_type ||
      iconType ||
      ""
    )?.toLowerCase();
    switch (brand) {
      case "visa":
        return <VisaIcon />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="Amex" />;
      case "mc":
      case "mastercard":
      case "master":
        return <MastercardIcon />;
      case "discover":
      case "ds":
        return <DiscoverIcon />;
      case "jcb":
        return <img src={JCB} alt="JCB" />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="Diners Club" />;
      case "unionpay":
        return <UnionPay />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (
      isEdit &&
      !initCountryRef.current &&
      selectedCountryUI &&
      countryList?.length > 0
    ) {
      const ev = countryList.find((c: any) => c?.value === selectedCountryUI);
      if (ev) {
        const nStates = getStatesForCountryCode(selectedCountryUI as string);
        setStates(nStates);
        handleChange(ev, "x_country");
        initCountryRef.current = true;
      }
    }
  }, [isEdit, selectedCountryUI, countryList, handleChange]);

  useEffect(() => {
    const getCountryAndState = async () => {
      try {
        const res: any = await dispatch(countryAndState()).unwrap();
        const data = res?.body?.data || [];
        setCountriesWithStates(data);
      } catch (error) {
        console.error(error);
      }
    };

    getCountryAndState();
  }, [dispatch]);

  const handleOnSubmit = async () => {
    setIsLoading(true);

    try {
      const result = await tokenizeCard();
      if (result) {
        const cc_token = result?.id;
        const cc_token2 = result?.customer_id;

        const response = await verifyStaxCardBank({
          cc_token,
          cc_token2,
          x_method: "CC",
        });
      }
    } catch (error) {
      console.error("Error in handleOnSubmit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Card details */}
      <div className="w-full">
        <div className="relative">
          <h2 className="text-txtSecondary text-xs font-bold uppercase mb-1">
            CARD INFORMATION
          </h2>

          <div
            className={`flex items-center border ${errorField === "stax-card-number" ? "!border-red" : ""} !rounded-[5px] !rounded-b-none !pl-[15px] !pr-[15px] !py-2 text-xs`}
          >
            <div id="stax-card-number" className="flex-1 h-[20px]" />

            {iconType?.length > 0 && (
              <div
                className={`absolute  right-4 top-1/2 transform -translate-y-1/2 text-xs capitalize ${
                  iconType ? "w-35px" : "w-174px"
                }`}
              >
                {imgUrl()}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2">
          <InputField
            name="x_exp_date"
            showLabel={false}
            className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !rounded-e-none  ${
              isErr === 2 ? "!border-red" : isErr === 1 ? "!border-t-0" : ""
            }`}
            _className="!text-xs !font-bold !leading-normal"
            placeholder="MM / YY"
            value={values?.x_exp_date}
            onChange={(e) => {
              handleChange(e, "x_exp_date");
            }}
            onBlur={validateCard}
            disabled={disableInputFields || isEdit}
          />

          <div
            className={`flex items-center border ${errorField === "stax-card-cvv" ? "!border-red" : ""} !pl-[15px] !pr-[15px] !py-2 text-xs !rounded-[5px] !rounded-t-none !rounded-s-none`}
          >
            <div id="stax-card-cvv" className="w-full h-[15px]" />
          </div>
        </div>
      </div>

      {/* Cardholder name */}
      <div className="w-full">
        <InputField
          name="choldername"
          label="Cardholder name"
          className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
          _className="!text-xs !font-bold !leading-normal"
          placeholder="Full name on card"
          value={values?.choldername}
          onChange={(e) => handleChange(e, "choldername")}
          tabInd={4}
          disabled={disableInputFields}
        />
      </div>

      {portalSettings?.cc_billing_address === SettingsValue.YES && (
        <div className="w-full">
          <InputSelect
            label="Billing Address"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
            _className="!text-xs !font-bold !leading-normal"
            optionClassName="!text-xs"
            arrowClassName="!right-[11px] !top-[10px]"
            placeholder="Country"
            selected={selectedCountryUI}
            options={countryList}
            setSelected={(e: any) => {
              autoDetectRef.current = true;
              const nStates = getStatesForCountryCode(e?.value as string);
              setStates(nStates);
              setSelectedState("");
              handleChange({ target: { value: "" } }, "x_state");
              handleChange(e, "x_country");
            }}
            tabInd={5}
            disabled={disableInputFields}
          />
          {fieldArr && fieldArr.includes("Address line 1") && (
            <InputField
              name="x_address"
              showLabel={false}
              className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                (fieldArr &&
                  (fieldArr.includes("City") ||
                    fieldArr.includes("Area") ||
                    fieldArr.includes("Address line 2"))) ||
                getPostalCode() ||
                getStateName()
                  ? "!rounded-none"
                  : "!rounded-b-[5px] !rounded-t-none"
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Address line 1"
              value={values?.x_address}
              onChange={(e) => handleChange(e, "x_address")}
              tabInd={6}
              disabled={disableInputFields}
            />
          )}
          {fieldArr && fieldArr.includes("Address line 2") && (
            <InputField
              name="address2"
              showLabel={false}
              className={`!pl-[15px] !pr-[15px] !py-2 !text-xs  !border-t-0 ${
                (fieldArr &&
                  (fieldArr.includes("City") || fieldArr.includes("Area"))) ||
                getPostalCode() ||
                getStateName()
                  ? "!rounded-none"
                  : "!rounded-b-[5px] !rounded-t-none"
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Address line 2"
              value={values?.address2}
              onChange={(e) => handleChange(e, "address2")}
              tabInd={7}
              disabled={disableInputFields}
            />
          )}
          <div
            className={`grid ${
              getPostalCode()
                ? !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                  ? "grid-cols-1"
                  : "grid-cols-2"
                : "grid-cols-1"
            }`}
          >
            {fieldArr && fieldArr.includes("City") && (
              <InputField
                name="x_city"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                  getStateName()
                    ? "!rounded-none"
                    : getPostalCode()
                      ? "rounded-b-[5px] !rounded-r-none"
                      : "!rounded-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="City"
                value={values?.x_city}
                onChange={(e) => handleChange(e, "x_city")}
                tabInd={8}
                disabled={disableInputFields}
              />
            )}
            {fieldArr && fieldArr.includes("Area") && (
              <InputField
                name="x_city"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                  getStateName()
                    ? "!rounded-none"
                    : getPostalCode()
                      ? "rounded-b-[5px] !rounded-r-none"
                      : "!rounded-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="Area"
                value={values?.x_city}
                onChange={(e) => handleChange(e, "x_city")}
                tabInd={8}
                disabled={disableInputFields}
              />
            )}

            {/* ['CEDEX' , 'ZIP' , 'Eircode' , 'PIN', "Postal code" ] */}
            {getPostalCode() && (
              <InputField
                name="x_zip"
                type={fieldObj?.isNumber ? "number" : "text"}
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                  !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                    ? ""
                    : "!border-l-0"
                } ${
                  getStateName()
                    ? "!rounded-none"
                    : "!rounded-b-[5px] !rounded-l-none !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder={getPostalCode() || ""}
                value={values?.x_zip}
                onChange={(e) => {
                  if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                    handleChange(e, "x_zip");
                }}
                tabInd={9}
                disabled={disableInputFields}
              />
            )}
          </div>
          {states && states.length > 0 && (
            <InputSelect
              showLabel={false}
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !border-t-0 "
              _className="!text-xs !font-bold !leading-normal"
              optionClassName="!text-xs"
              placeholder={getStateName() || "State"}
              selected={selectedState}
              arrowClassName="!right-[11px] !top-[10px]"
              isManual
              options={states}
              setSelected={(e: any) => {
                if (e?.value) {
                  setSelectedState(e);
                  handleChange(e, "x_state");
                }
              }}
              tabInd={10}
              disabled={disableInputFields}
            />
          )}
        </div>
      )}

      {portalSettings?.cc_billing_address === SettingsValue.NO && (
        <div className="w-full">
          <InputSelect
            label="Billing Address"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
            _className="!text-xs !font-bold !leading-normal"
            optionClassName="!text-xs"
            arrowClassName="!right-[11px] !top-[10px]"
            placeholder="Country"
            selected={selectedCountryUI}
            options={countryList}
            setSelected={(e: any) => {
              autoDetectRef.current = true;
              const nStates = getStatesForCountryCode(e?.value as string);
              setStates(nStates);
              setSelectedState("");
              handleChange({ target: { value: "" } }, "x_state");
              handleChange(e, "x_country");
            }}
            tabInd={5}
            disabled={disableInputFields}
          />
          {getPostalCode() && (
            <InputField
              name="x_zip"
              type={fieldObj?.isNumber ? "number" : "text"}
              showLabel={false}
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-b-[5px] !border-t-0 !rounded-t-none "
              _className="!text-xs !font-bold !leading-normal"
              placeholder={getPostalCode() || ""}
              value={values?.x_zip}
              onChange={(e) => {
                if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                  handleChange(e, "x_zip");
              }}
              tabInd={9}
              disabled={disableInputFields}
            />
          )}
        </div>
      )}

      <div>
        {showConvFee &&
          portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
            <ConvenienceFeeDisplay
              conFee={conFee}
              isFeeLoading={isFeeLoading}
              isPaymentLink={isPaymentLink}
              setShowPreview={setShowPreview}
              isStaxGateway={true}
              isTokenAvailable={false}
            />
          )}

        {tokenError ? (
          <div className="text-red text-xs mt-0.5">{tokenError}</div>
        ) : isErr === 2 ? (
          <div className="text-red text-xs mt-0.5">{cardErrMsg}</div>
        ) : (
          <></>
        )}
      </div>

      <div className="relative w-full pt-5">
        <InputField
          name="Email"
          label="Email"
          className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
          _className="!text-xs !font-bold !leading-normal"
          placeholder="Email address"
          onBlur={handleEmailBlur}
          onChange={(e) => {
            setEmail(
              userData?.data?.accountData?.email
                ? userData?.data?.accountData?.email
                : e.target.value
            );
          }}
          value={
            userData?.data?.accountData?.email
              ? userData?.data?.accountData?.email
              : email
          }
          disabled={userData?.data?.accountData?.email}
        />
      </div>

      {collectPhone && (
        <div className="pt-5">
          <div
            className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
          >
            Phone
          </div>
          <MobileInputNew
            inputValue={phoneNumber?.phone || ""}
            isProductLinkInput={true}
            userCountry={getCountryShortName(phoneNumber?.country_code) || "US"}
            setCountry={handlePhoneCountryChange}
            setInputValue={handleOnPhoneChange}
          />
        </div>
      )}

      {/* Pay Button */}
      {!isRetry && (
        <div className="my-5">
          {isAgreement ? (
            <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
              <Button
                text={`Back`}
                variant="white"
                onClick={handleBack}
                isDisabled={isLoading}
                className="lg:w-40 w-full max-w-40"
              />
              <Button
                text={`Confirm`}
                isDisabled={isDisabled || amountCheck}
                isLoading={isLoading}
                onClick={() => {
                  handleOnSubmit();
                }}
                className="lg:w-40 w-full max-w-40"
                variant="variable"
              />
            </div>
          ) : isSchedulePayment ? (
            <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
              <div
                className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
              >
                {isUpdatePlan && (
                  <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                )}

                <div className="w-full flex justify-between gap-5">
                  <Button
                    className="lg:!w-40 !w-full"
                    isDisabled={isLoading}
                    text="Back"
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                  />
                  <Button
                    className="lg:!w-40 !w-full"
                    isLoading={isLoading}
                    isDisabled={isDisabled || amountCheck}
                    text={isUpdatePlan ? "Update" : "Confirm"}
                    type="button"
                    onClick={() => {
                      handleOnSubmit();
                    }}
                    variant="variable"
                  />
                </div>
              </div>
            </div>
          ) : (
            <Button
              text={
                isUpdatePlan || isUpdateSubscription
                  ? "Update"
                  : `Pay ${formatCurrency(getPaymentTotalWithFee(amount, conFee)) || 0.0}`
              }
              className={"!w-full mt-5"}
              isDisabled={
                isDisabled ||
                amountCheck ||
                (isProductLink && !isSubscriptionAllowed)
              }
              onClick={() => {
                handleOnSubmit();
              }}
              isLoading={isLoading || isFeeLoading || isChecking}
              variant={"variable"}
            />
          )}
        </div>
      )}

      {/* {isRetry && (
        <div className="w-full flex justify-end pt-8">
          <Button
            text="Next"
            className="!px-14"
            isDisabled={isDisabled || isFeeLoading}
            isLoading={isFeeLoading}
            variant="variable"
            onClick={() => {
              handleRetryNextStore();
            }}
          />
        </div>
      )} */}
    </div>
  );
};

export default StaxCardForm;
